import api, { API_BASE, defaultOpt, noCache } from "./apiService";
import axios from "axios";
import {
  createBlobDownload,
  dateYYYYMMMDD,
  validatePositiveInt,
} from "./commonParse";

export async function getAllBP() {
  const content = (
    await api.get(
      `${API_BASE}/list-bp`,
      defaultOpt({ "Accept-Encoding": "gzip" }),
    )
  ).data;
  return content;
}

export async function deleteBP(id) {
  const response = await axios
    .delete(`${API_BASE}/delete-bp/${id}`, defaultOpt(noCache))
    .catch((error) => console.log("Error: ", error));
  if (response && response.data) {
    return response.data;
  }
}

export async function addBP(form) {
  return api.post(`${API_BASE}/add-bp`, form, defaultOpt()).data;
}

export async function getBP(id) {
  return (await api.get(`${API_BASE}/get-bp/${id}`)).data;
}

export async function updateBP(id, form) {
  return (await api.post(`${API_BASE}/update-bp/${id}`, form, defaultOpt()))
    .data;
}

export async function updateBPBatch(form) {
  return (await api.post(`${API_BASE}/update-bp-batch`, form, defaultOpt()))
    .data;
}

export async function createDateBinning(
  typelist,
  datemethod,
  catmethod,
  interval,
) {
  return (
    await api.get(
      `${API_BASE}/export-date-binning?typelist=${typelist.join(",")}&datemethod=${datemethod}&catmethod=${catmethod}&interval=${interval}`,
    )
  ).data;
}

export async function createCodeList(barcode_list, comment) {
  const blob = new Blob([barcode_list.join("\n")], { type: "text/plain" });
  const filename = `KIT_barcode_list_${comment.replace(/\s+/g, "_")}_${dateYYYYMMMDD()}.txt`;
  createBlobDownload(blob, filename);
}

export async function createXMLnewparts(idlist, comment) {
  const { data } = await api.get(
    `${API_BASE}/export-xml-newparts/${idlist.join(",")}`,
  );
  if (data) {
    const blob = new Blob([data], { type: "text/plain" });
    const filename = `KIT_newparts_${comment.replace(/\s+/g, "_")}_${dateYYYYMMMDD()}.xml`;
    createBlobDownload(blob, filename);
  }
  return data;
}

export async function createXMLqcgrade(idlist, comment) {
  const { data } = await api.get(
    `${API_BASE}/export-xml-qcgrade/${idlist.join(",")}`,
  );
  if (data) {
    const blob = new Blob([data], { type: "text/plain" });
    const filename = `KIT_qcgrade_${comment.replace(/\s+/g, "_")}_${dateYYYYMMMDD()}.xml`;
    createBlobDownload(blob, filename);
  }
  return data;
}

const __map__ = {
  // Geometry type map
  F0W: "CEE Full",
  F0T: "CEH Full",
  FLW: "CEE LD Full",
  FHW: "CEE HD Full",
  FLT: "CEH LD Full",
  FHT: "CEH HD Full",
  BLW: "CEE LD Bottom",
  BLT: "CEH LD Bottom",
  TLW: "CEH LD Top",
  TLT: "CEH LD Top",
  "5LW": "CEE LD Five",
  "5LT": "CEH LD Five",
  LLW: "CEE LD Left",
  RLW: "CEE LD Right",
  SLW: "CEE LD Semi",
  LLT: "CEH LD Left",
  RLT: "CEH LD Right",
  LHW: "CEE HD Left",
  RHW: "CEE HD Right",
  SHW: "CEE HD Semi",
  BHW: "CEE HD Bottom",
  THT: "CEH HD Top",
};

export const makeBareTypeOtions = () => {
  const map = {
    // Full plates
    F0W: "CuW/CEE Full",
    F0T: "Ti/CEH Full",
    // Ti Partial
    BLT: "Ti/CEH LD Bottom",
    TLT: "Ti/CEH LD Top",
    "5LT": "Ti/CEH LD Five",
    LLT: "Ti/CEH LD Left",
    RLT: "Ti/CEH LD Right",
    THT: "Ti/CEH HD Top",
    // CuW partials
    BLW: "CuW/CEE LD Bottom",
    TLW: "CuW/CEE LD Top",
    "5LW": "CuW/CEE LD Five",
    SLW: "CuW/CEE LD Semi",
    SHW: "CuW/CEE HD Semi",
    BHW: "CuW/CEE HD Bottom",
  };
  return Object.keys(map).map((k) => {
    return { value: k, label: `[${k}] ${map[k]}` };
  });
};

// Additional methods for validating if user input is a valid CMS barcode
export const validLocationCodes = ["KA", "IH", "II", "CI"];

function bc_common(value, pre, allowedType) {
  if (value === "" || value === null) {
    return ""; //Early return for null item
  }
  if (value.length !== 15) {
    return `CMS barcode must be 15 characters long (got ${value.length}, ${value}).`;
  }
  const prefix = value.substring(0, 5);
  const bpType = value.substring(5, 8);
  const loc = value.substring(8, 10);
  const idx = value.substring(10, 15);
  if (prefix !== pre) {
    return `Expect prefix [${pre}].`;
  }
  if (!allowedType.includes(bpType)) {
    return `Unrecogznied baseplate type [${bpType}].`;
  }
  if (!validLocationCodes.includes(loc)) {
    return `Unrecognized location index [${loc}].`;
  }
  if (!/^\d{5}$/.test(idx)) {
    return `Last five indices should be numbers (got ${idx}).`;
  }
  return "";
}

function exists_bp(value, selfid, bpList, field, field_name) {
  if (!("id" in bpList)) return "";

  for (let idx = 0; idx < bpList.id.length; ++idx) {
    if (
      bpList[field][idx] !== null &&
      bpList[field][idx] === value &&
      bpList.id[idx] !== selfid
    ) {
      return `${field_name} [${value}] already exists in database`;
    }
  }
  return "";
}

export function validateCMSbarcode(id, bpList) {
  const allowed = Object.keys(__map__).filter(
    (x) => x[1] !== "0" && x[0] !== "S",
  );
  return (value) => {
    if (value === "") return ""; // Allow empty values
    const error = bc_common(value, "320BA", allowed);
    return error !== ""
      ? error + " Expected: [320BAtttllddddd]"
      : exists_bp(value, id, bpList, "bar_code", "CMS barcode");
  };
}

export function validateBareBarcode(id, bpList) {
  const allowed = Object.keys(__map__).filter((x) => {
    // if (x.startsWith("F")) return x[1] === "0";
    if (x.endsWith("W")) return x[0] !== "R" && x[0] !== "L";
    return true;
  });
  return (value) => {
    if (value === "") return ""; // Allow empty values
    const error = bc_common(value, "320BP", allowed);
    return error !== ""
      ? error + " Expected: [320BPtttllddddd]"
      : exists_bp(value, id, bpList, "bare_barcode", "Bare barcode");
  };
}

export function validateWS(id, bpList) {
  const pintValidate = validatePositiveInt();
  return (value) => {
    const error = pintValidate(value);
    return error !== ""
      ? error
      : exists_bp(value, id, bpList, "workshop_serial", "Workshop serial");
  };
}

export function validateIdentifier(bpList) {
  return (value) => {
    if (value === null) return "";
    const ws = bpList.workshop_serial.find(
      (ws) => Number(ws) === Number(value),
    );
    if (ws) return "";
    if (value.length === 15 && value.startsWith("320BP")) {
      const bc_idx = bpList.bare_barcode.indexOf(value);
      if (bc_idx !== -1) return "";
    }
    return "Requires a valid workshop serial or bare barcode";
  };
}

export function baseplateGeometryDisplay(type) {
  return type in __map__ ? __map__[type] : "Unknown";
}

export function projectTotal(type, spare = false) {
  // Special calculation for FLW and FHW to account for IHEP K-order

  const IHEPTotal = 10000;
  if (type === "FLW") {
    const hgcTotal = 10234 * (spare ? 1.05 : 1.0); // Number from grand tally spreadsheet
    return Math.ceil(hgcTotal - 0.8 * IHEPTotal);
  } else if (type === "FHW") {
    const hgcTotal = 3810 * (spare ? 1.05 : 1.0);
    return Math.ceil(hgcTotal - 0.2 * IHEPTotal);
  }

  return Math.ceil(
    {
      // FHW: 1937,
      // FLW: 2951,
      LLW: 277,
      RLW: 214,
      TLW: 252,
      BLW: 517,
      "5LW": 653,
      LHW: 164,
      RHW: 164,
      BHW: 328,
      FHT: 970,
      FLT: 8505,
      LLT: 88,
      RLT: 164,
      TLT: 479,
      BLT: 315,
      "5LT": 605,
      THT: 88,
    }[type] * (spare ? 1.05 : 1),
  );
}
